import type { Identificable } from 'shared/types/commonView';

/** Type for entities that may have an optional _id */
interface MaybeIdentificable {
  _id?: string | null;
}

/**
 * Type guard to check if an entity has a valid (non-empty) _id.
 * Useful for filtering entities before operations that require _id.
 */
export const hasValidId = <T extends MaybeIdentificable>(
  item: T
): item is T & { _id: string } =>
  typeof item._id === 'string' && item._id.length > 0;

export const idsFromEntities = <T extends Identificable<string>>(
  entities: readonly T[]
): string[] => entities.map((entity) => entity._id);

export const createEntityMapById = <T extends Identificable<string>>(
  entities: readonly T[]
): Map<string, T> =>
  entities.reduce((result, entity) => {
    result.set(entity._id, entity);
    return result;
  }, new Map<string, T>());

export const createIdSet = (ids: readonly string[]): Set<string> =>
  new Set(ids);

export const filterEntitiesByIdSet = <T extends Identificable<string>>(
  entities: readonly T[],
  ids: ReadonlySet<string>
): T[] => entities.filter((entity) => ids.has(entity._id));
